import { css } from "@emotion/css";
import { Badge, Callout, InlineEmptyStateOnboarding, Tab, Tabs, useShowSnackbar } from "@octopusdeploy/design-system-components";
import { space, themeTokens } from "@octopusdeploy/design-system-tokens";
import { useQuery } from "@octopusdeploy/octopus-react-client";
import type { ActionProperties, ChannelResource, EnvironmentV2Resource, GetTeamsLookupBffResponseTeam, GitRefResource, ProcessTemplateListSummaryResource, ProcessTemplateResource, ProcessTemplateVersionSummary, ProjectSummaryResource, PropertyValueResource, VariableSetResource, WorkerPoolResource, } from "@octopusdeploy/octopus-server-client";
import { generateSlug } from "@octopusdeploy/utilities";
import { isEmpty, isEqual } from "lodash";
import * as React from "react";
import { useEffect, useState } from "react";
import semver from "semver/preload";
import type { ProcessTemplateConsumerActionDispatcher } from "~/analytics/Analytics";
import { Action, useAnalyticProcessTemplateConsumerActionDispatch, useAnalyticsStepEditorDispatch } from "~/analytics/Analytics";
import { CloudConnectionUsageType, getCloudConnections, isCloudTargetDiscoveryEnabled } from "~/areas/projects/components/CloudConnection/CloudConnections";
import { deleteActionAndRedirect, getDeleteProcessMenuItem, isMatchingProcessTemplate, processScopedEditPermission } from "~/areas/projects/components/Process/Common/CommonProcessHelpers";
import { ErrorsForAction } from "~/areas/projects/components/Process/Common/ErrorsForAction";
import StepName from "~/areas/projects/components/Process/Common/StepName";
import { useProcessBlueprintsFromContext } from "~/areas/projects/components/Process/Contexts/ProcessBlueprintsContextProvider";
import { useProcessContext } from "~/areas/projects/components/Process/Contexts/ProcessContext";
import type { ProcessTemplateDetails } from "~/areas/projects/components/Process/Contexts/ProcessController";
import { useProcessErrorSelectors } from "~/areas/projects/components/Process/Contexts/ProcessErrors/ProcessErrorsContext";
import { useProcessQueryStringContext } from "~/areas/projects/components/Process/Contexts/ProcessQueryString/ProcessQueryStringContext";
import type { FailedPermissionCheck, ProcessStepActionState } from "~/areas/projects/components/Process/ProcessStepsLayoutTypes";
import { isFailedPermissionCheck } from "~/areas/projects/components/Process/ProcessStepsLayoutTypes";
import { ProcessSubPageLayout } from "~/areas/projects/components/Process/ProcessSubPageLayout";
import { ProcessTemplateActionParameterValues, requiresValue } from "~/areas/projects/components/Process/ProcessTemplates/ProcessTemplateActionParameterValues";
import { ProcessTemplateSelector } from "~/areas/projects/components/Process/ProcessTemplates/ProcessTemplateSelector";
import { getVersionMask, mapMaskToUpdateBehaviour, ProcessTemplateVersioningSection, UpdateBehaviour, UsageConstraint } from "~/areas/projects/components/Process/ProcessTemplates/ProcessTemplateVersioningSection";
import { getProcessTemplateSlugFromAction, getProcessTemplateVersionMaskFromAction } from "~/areas/projects/components/Process/ProcessTemplates/processTemplateId";
import styles from "~/areas/projects/components/Process/style.module.less";
import type { StoredAction, StoredStep } from "~/areas/projects/components/Process/types";
import { isDeploymentProcessIdentifier, isDeploymentOrRunbookProcessIdentifier } from "~/areas/projects/components/Process/types";
import type { LoadedLibraryVariableSets } from "~/areas/projects/components/Variables/AllVariables/SharedAllVariablesPage";
import getActionLogoUrl from "~/areas/projects/components/getActionLogoUrl";
import ActionList from "~/components/ActionList/index";
import pluginRegistry from "~/components/Actions/pluginRegistry";
import type { DoBusyTask } from "~/components/DataBaseComponent/index";
import { ExpandableContainer } from "~/components/Expandable/index";
import { useIsLicensedFeatureEnabled } from "~/components/LicensedFeature/useIsLicensedFeatureEnabled";
import Logo from "~/components/Logo/index";
import type { MenuItem } from "~/components/OverflowMenu/OverflowMenu";
import { OverflowMenu, OverflowMenuItems } from "~/components/OverflowMenu/OverflowMenu";
import { ExpandableFormSection, FormSection, FormSectionHeading, required, Summary, UnstructuredFormSection } from "~/components/form/index";
import NameSummaryWithSlug from "~/primitiveComponents/form/Slugs/NameSummaryWithSlug";
import SlugEditor from "~/primitiveComponents/form/Slugs/SlugEditor";
import { DebounceText } from "~/primitiveComponents/form/Text/Text";
interface ProcessTemplateStepDetailsProps {
    step: StoredStep;
    action: StoredAction;
    stepOther: ProcessStepActionState;
    isNew: boolean;
    cleanAction: StoredAction | null;
    doBusyTask: DoBusyTask;
    gitRefResource: GitRefResource | undefined;
    releaseCreationPackageStepId: string | undefined;
    processVariableSet: VariableSetResource | FailedPermissionCheck;
    libraryVariableSets: LoadedLibraryVariableSets[] | FailedPermissionCheck;
    environments: EnvironmentV2Resource[];
    availableTargetTags: string[];
    availableWorkerPools: WorkerPoolResource[];
    teams: GetTeamsLookupBffResponseTeam[];
    isTenanted: boolean;
    channels: ChannelResource[];
    projects: ProjectSummaryResource[];
    refreshVariables: () => Promise<void>;
}
export type ConsumerProcessTemplateEditorUndefinedTab = "";
export type ConsumerProcessTemplateEditorTabs = "parameters" | "settings" | ConsumerProcessTemplateEditorUndefinedTab;
export const isConsumerProcessTemplateEditorTab = (value: string): value is ConsumerProcessTemplateEditorTabs => {
    return value === "parameters" || value === "settings" || value === "";
};
export function ProcessTemplateStepDetails({ step, action, stepOther, isNew, cleanAction, doBusyTask, gitRefResource, releaseCreationPackageStepId, processVariableSet, libraryVariableSets, environments, availableTargetTags, availableWorkerPools, isTenanted, teams, channels, projects, refreshVariables, }: ProcessTemplateStepDetailsProps) {
    const processContext = useProcessContext();
    const processErrorSelectors = useProcessErrorSelectors();
    const processTemplateContext = useProcessBlueprintsFromContext();
    const isFeatureEnabled = useIsLicensedFeatureEnabled("ProcessTemplates");
    const processIdentifier = processContext.state.processIdentifier;
    if (!isDeploymentOrRunbookProcessIdentifier(processIdentifier)) {
        throw new Error("Process templates can only be used in deployment or runbook processes.");
    }
    const analyticsStepEditorDispatch = useAnalyticsStepEditorDispatch(step.Id, processIdentifier.projectId);
    const processTemplateConsumerActionDispatch = useAnalyticProcessTemplateConsumerActionDispatch(processIdentifier.projectId, isDeploymentProcessIdentifier(processIdentifier) ? "Deployment Process" : "Runbook");
    const processTemplateSlug = getProcessTemplateSlugFromAction(action);
    const processTemplateVersionMask = getProcessTemplateVersionMaskFromAction(action);
    const actionErrors = action ? processErrorSelectors.getActionErrors(action.Id, processContext.selectors) : [];
    const [initialActionProperties, setInitialActionProperties] = useState(action.Properties);
    const [initialCleanModel, setInitialCleanModel] = useState(processContext.state.cleanModel);
    if (!isEqual(initialCleanModel, processContext.state.cleanModel)) {
        setInitialCleanModel(processContext.state.cleanModel);
        setInitialActionProperties(action.Properties);
    }
    const processQueryStringContext = useProcessQueryStringContext();
    const [activeTab, setActiveTab] = useState<ConsumerProcessTemplateEditorTabs>(processQueryStringContext.state.queryFilter.activeTab ? processQueryStringContext.state.queryFilter.activeTab : "parameters");
    const selectedProcessTemplateDetails = processTemplateContext.find((pt) => isMatchingProcessTemplate(pt, processTemplateSlug, processTemplateVersionMask));
    const selectedProcessTemplate = selectedProcessTemplateDetails?.processTemplate;
    const [initialProcessTemplateVersion, setInitialProcessTemplateVersion] = useState<string | undefined | null>(selectedProcessTemplateDetails?.processTemplateVersion);
    const getFieldError = (value: string) => {
        return processErrorSelectors.getActionFieldError(action.Id, processContext.selectors, value);
    };
    const setActionMetaProperties: <K extends keyof StoredAction>(state: Pick<StoredAction, K> | StoredAction, callback?: () => void) => void = (state, callback) => {
        processContext.actions.setActionMetaProperties(action.Id, (prev) => ({ ...prev, ...state }));
        callback && callback();
    };
    const setName = (value: string) => {
        setActionMetaProperties({ Name: value });
    };
    const setSlug = (value: string) => {
        setActionMetaProperties({ Slug: value });
    };
    const setDisabled = (value: boolean) => {
        setActionMetaProperties({ IsDisabled: value });
    };
    const setVersionMaskProperty = (versionMask: string) => {
        const properties: ActionProperties = {
            "Octopus.Action.ProcessTemplate.Reference.VersionMask": versionMask,
        };
        processContext.actions.setActionProperties(action.Id, properties);
    };
    const setProcessTemplateSlugProperty = (slug: string) => {
        const properties: ActionProperties = {
            "Octopus.Action.ProcessTemplate.Reference.Slug": slug,
        };
        processContext.actions.setActionProperties(action.Id, properties);
    };
    const setParameterValue = (parameterName: string, value: PropertyValueResource | undefined) => {
        if (value) {
            const properties: ActionProperties = { [parameterName]: value };
            processContext.actions.setActionProperties(action.Id, properties);
        }
        else {
            processContext.actions.removeActionProperties(action.Id, [parameterName]);
        }
    };
    const { result: distinctTemplates } = useQuery((repository) => repository.ProcessTemplates.summaries(), [], "Get Templates");
    const [selectedProcessTemplateSlug, setSelectedProcessTemplateSlug] = useState<string | undefined>(isEmpty(processTemplateSlug) ? undefined : processTemplateSlug);
    const [selectedUsageConstraint, setSelectedUsageConstraint] = useState<UsageConstraint>(mapMaskToUsageConstraint(processTemplateVersionMask));
    const [selectedUpdateBehaviour, setSelectedUpdateBehaviour] = useState<UpdateBehaviour>(mapMaskToUpdateBehaviour(processTemplateVersionMask));
    const { result: latestProcessTemplateVersionSummary } = useQuery(async (repository) => {
        let result: ProcessTemplateVersionSummary | null = null;
        if (selectedProcessTemplateSlug) {
            result = await repository.ProcessTemplates.getLatestVersionSummary(selectedProcessTemplateSlug);
        }
        return result;
    }, [selectedProcessTemplateSlug], "Get Latest Published Process Template Summary");
    useEffect(() => {
        if (!latestProcessTemplateVersionSummary) {
            return;
        }
        setVersioningDefaults(latestProcessTemplateVersionSummary);
    }, 
    // We intentionally only want this to run when the summary changes, as this means the process template has changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [latestProcessTemplateVersionSummary]);
    const showSnackbar = useShowSnackbar();
    const latestStableVersion = latestProcessTemplateVersionSummary?.LatestStableVersion;
    const selectedProcessTemplateVersion = selectedProcessTemplateDetails?.processTemplateVersion;
    const isUpdateAvailable = selectedProcessTemplateVersion && latestStableVersion ? semver.ltr(selectedProcessTemplateVersion, latestStableVersion) : false;
    const onUpdateToLatestVersion = () => {
        if (latestStableVersion) {
            if (selectedUsageConstraint === UsageConstraint.PreRelease) {
                setSelectedUsageConstraint(UsageConstraint.Stable);
                setSelectedUpdateBehaviour(UpdateBehaviour.Minor);
                const versionMask = getVersionMask(latestStableVersion, UpdateBehaviour.Minor);
                setVersionMaskProperty(versionMask);
            }
            else {
                const versionMask = getVersionMask(latestStableVersion, selectedUpdateBehaviour);
                setVersionMaskProperty(versionMask);
            }
            setInitialProcessTemplateVersion(latestStableVersion);
            showSnackbar("Process template updated to latest version.");
        }
    };
    const requiredParametersCount = selectedProcessTemplate?.Parameters.filter((p) => requiresValue(p, action, availableWorkerPools, isTenanted)).length ?? 0;
    const onTabChange = (_: React.ChangeEvent<object>, newValue: string) => {
        if (isConsumerProcessTemplateEditorTab(newValue)) {
            setActiveTab(newValue);
            processQueryStringContext.actions.changeProcessTemplateEditorActiveTab(newValue);
        }
    };
    return (<>
            <ExpandableContainer containerKey={action.Id}>
                <ActionLayout step={step} action={action} stepOther={stepOther} setDisabled={setDisabled} releaseCreationPackageStepId={releaseCreationPackageStepId} templateVersion={initialProcessTemplateVersion}>
                    <>
                        <ActionDisabledWarning action={action}/>
                        <ErrorsForAction actionErrors={actionErrors}/>
                        <Tabs value={activeTab} onChange={onTabChange} variant="scrollable">
                            <Tab value="parameters" label={<div className={tabLabelStyles}>
                                        <div>Parameters</div>
                                        <Badge size="small" variant="neutral" count={requiredParametersCount}/>
                                    </div>}/>
                            <Tab value="settings" label={isUpdateAvailable && isFeatureEnabled ? (<div className={tabLabelStyles}>
                                            <div>Settings</div>
                                            <Badge size="small" variant="neutral" label="Update available"/>
                                        </div>) : ("Settings")}/>
                        </Tabs>
                        {distinctTemplates ? (<>
                                {activeTab === "parameters" &&
                (selectedProcessTemplate ? (<ProcessTemplateActionParameterValues stepId={step.Id} action={action} doBusyTask={doBusyTask} onParameterValueChanged={setParameterValue} processTemplate={selectedProcessTemplate} gitRefResource={gitRefResource} libraryVariableSets={libraryVariableSets} processVariableSet={processVariableSet} environments={environments} availableWorkerPools={availableWorkerPools} availableTargetTags={availableTargetTags} analyticsStepEditorDispatch={analyticsStepEditorDispatch} isTenanted={isTenanted} teams={teams} channels={channels} projects={projects} refreshVariables={refreshVariables}/>) : (<>
                                            <div className={borderStyles}/>
                                            <InlineEmptyStateOnboarding illustration="NoEntities" entityName="parameters" title="No parameters found" description="This template may not have any parameters, or you haven’t selected a template yet."/>
                                        </>))}
                                {activeTab === "settings" && (<ProcessTemplateSettings distinctTemplates={distinctTemplates} selectedProcessTemplate={selectedProcessTemplate} isNew={isNew} action={action} getFieldError={getFieldError} setName={setName} cleanAction={cleanAction} setSlug={setSlug} initialActionProperties={initialActionProperties} initialProcessTemplateVersion={initialProcessTemplateVersion} selectedProcessTemplateDetails={selectedProcessTemplateDetails} setProcessTemplateSlugProperty={setProcessTemplateSlugProperty} setVersionMaskProperty={setVersionMaskProperty} latestProcessTemplateVersionSummary={latestProcessTemplateVersionSummary} selectedProcessTemplateSlug={selectedProcessTemplateSlug} setSelectedProcessTemplateSlug={setSelectedProcessTemplateSlug} selectedUpdateBehaviour={selectedUpdateBehaviour} setSelectedUpdateBehaviour={setSelectedUpdateBehaviour} selectedUsageConstraint={selectedUsageConstraint} setSelectedUsageConstraint={setSelectedUsageConstraint} processVariableSet={processVariableSet} libraryVariableSets={libraryVariableSets} processTemplateConsumerActionDispatch={processTemplateConsumerActionDispatch} onUpdateToLatestVersion={onUpdateToLatestVersion} currentVersion={selectedProcessTemplateVersion} latestStableVersion={latestStableVersion}/>)}
                            </>) : null}
                    </>
                </ActionLayout>
            </ExpandableContainer>
        </>);
    function setVersioningDefaults(latestProcessTemplateVersionSummary: ProcessTemplateVersionSummary) {
        if (latestProcessTemplateVersionSummary.IsOnlyPreRelease) {
            // Set a default when there are only prerelease versions available
            setVersionMaskProperty(UpdateBehaviour.PreRelease);
            setSelectedUsageConstraint(UsageConstraint.PreRelease);
            setSelectedUpdateBehaviour(UpdateBehaviour.PreRelease);
        }
        else if (processTemplateVersionMask === "" || selectedUpdateBehaviour === UpdateBehaviour.Invalid) {
            // Set a default when there are stable versions available and either:
            // - First adding a process template step
            // - The process template has changed
            // - The provided version mask is invalid
            if (!latestProcessTemplateVersionSummary.LatestStableVersion) {
                throw Error("Cannot set version mask when no stable version is available.");
            }
            const mappedVersionMask = getVersionMask(latestProcessTemplateVersionSummary.LatestStableVersion, UpdateBehaviour.Minor);
            setVersionMaskProperty(mappedVersionMask);
            setSelectedUsageConstraint(UsageConstraint.Stable);
            setSelectedUpdateBehaviour(UpdateBehaviour.Minor);
        }
    }
}
const tabLabelStyles = css({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: space[8],
});
const borderStyles = css({
    marginTop: space[1],
    borderTop: `${space[1]} solid ${themeTokens.color.processStep.border.default}`,
});
interface ActionLayoutProps {
    step: StoredStep;
    action: StoredAction;
    stepOther: ProcessStepActionState;
    setDisabled: (value: boolean) => void;
    children: JSX.Element;
    releaseCreationPackageStepId: string | undefined;
    templateVersion: string | null | undefined;
}
function ActionLayout({ step, action, setDisabled, stepOther, releaseCreationPackageStepId, children, templateVersion }: ActionLayoutProps) {
    const processContext = useProcessContext();
    const processIdentifier = processContext.state.processIdentifier;
    const { actions: contextActions, selectors: contextSelectors } = processContext;
    const { actions: queryStringActions } = useProcessQueryStringContext();
    const stepActionNumber = `${contextSelectors.getStepNumber(step.Id)}`;
    const title = <StepName name={action.Name} number={stepActionNumber} stepType={stepOther.actionTypeName}/>;
    const logo = <Logo url={getActionLogoUrl(action)}/>;
    const menuItems: MenuItem[] = [];
    if (step && step.Id) {
        const processEditPermission = {
            permission: processScopedEditPermission(processContext.selectors.getProcessType()),
            project: isDeploymentOrRunbookProcessIdentifier(processIdentifier) ? processIdentifier.projectId : undefined,
            wildcard: true,
        };
        const enableItem = OverflowMenuItems.item(action.IsDisabled ? "Enable" : "Disable", () => setDisabled(!action.IsDisabled), processEditPermission);
        const deleteItem = getDeleteProcessMenuItem("step", async () => deleteActionAndRedirect(step, action, true, contextActions, contextSelectors, queryStringActions), processEditPermission, releaseCreationPackageStepId, processIdentifier, step, action, processContext);
        menuItems.push(enableItem, deleteItem);
    }
    const controlActions = [<OverflowMenu menuItems={menuItems} accessibleName="More Step Actions"/>];
    const sectionControl = <ActionList actions={controlActions}/>;
    return (<ProcessSubPageLayout title={title} titleLogo={logo} titleBadge={templateVersion ? `v${templateVersion}` : undefined} sectionControl={sectionControl}>
            {children}
        </ProcessSubPageLayout>);
}
function buildNameSummary(action: StoredAction) {
    if (!action.Name) {
        return Summary.placeholder("Please enter a name for your step");
    }
    const name = (<div className={styles.nameSummaryContainer}>
            <NameSummaryWithSlug name={action.Name} slug={action.Slug || null}/>
        </div>);
    return Summary.summary(name);
}
interface ActionDisabledWarningProps {
    action: StoredAction;
}
function ActionDisabledWarning({ action }: ActionDisabledWarningProps) {
    if (!action.IsDisabled) {
        return null;
    }
    return (<UnstructuredFormSection stretchContent={true}>
            <Callout type={"warning"} title={"This step is currently disabled"}/>
        </UnstructuredFormSection>);
}
interface ProcessTemplateSettingsProps {
    distinctTemplates: ProcessTemplateListSummaryResource[] | undefined;
    selectedProcessTemplate: ProcessTemplateResource | undefined;
    isNew: boolean;
    action: StoredAction;
    getFieldError: (value: string) => string | undefined;
    setName: (value: string) => void;
    cleanAction: StoredAction | null;
    setSlug: (value: string) => void;
    initialActionProperties: ActionProperties;
    initialProcessTemplateVersion: string | undefined | null;
    selectedProcessTemplateDetails: ProcessTemplateDetails | undefined;
    setProcessTemplateSlugProperty: (slug: string) => void;
    setVersionMaskProperty: (versionMask: string) => void;
    latestProcessTemplateVersionSummary: ProcessTemplateVersionSummary | null;
    selectedProcessTemplateSlug: string | undefined;
    setSelectedProcessTemplateSlug: (value: string | undefined) => void;
    selectedUpdateBehaviour: UpdateBehaviour;
    setSelectedUpdateBehaviour: React.Dispatch<React.SetStateAction<UpdateBehaviour>>;
    selectedUsageConstraint: UsageConstraint;
    setSelectedUsageConstraint: React.Dispatch<React.SetStateAction<UsageConstraint>>;
    processVariableSet: VariableSetResource | FailedPermissionCheck;
    libraryVariableSets: LoadedLibraryVariableSets[] | FailedPermissionCheck;
    processTemplateConsumerActionDispatch: ProcessTemplateConsumerActionDispatcher;
    onUpdateToLatestVersion: () => void;
    currentVersion: string | undefined;
    latestStableVersion: string | undefined | null;
}
function ProcessTemplateSettings({ distinctTemplates, selectedProcessTemplate, isNew, action, getFieldError, setName, cleanAction, setSlug, initialActionProperties, initialProcessTemplateVersion, selectedProcessTemplateDetails, setProcessTemplateSlugProperty, setVersionMaskProperty, latestProcessTemplateVersionSummary, selectedProcessTemplateSlug, setSelectedProcessTemplateSlug, selectedUpdateBehaviour, setSelectedUpdateBehaviour, selectedUsageConstraint, setSelectedUsageConstraint, processVariableSet, libraryVariableSets, processTemplateConsumerActionDispatch, onUpdateToLatestVersion, currentVersion, }: ProcessTemplateSettingsProps) {
    if (!distinctTemplates) {
        return null;
    }
    const isProcessTemplateSelected = !!selectedProcessTemplateSlug;
    const isProcessTemplateUnavailable = isProcessTemplateSelected && !selectedProcessTemplateDetails && !latestProcessTemplateVersionSummary;
    const onProcessTemplateChanged = (value: string | undefined) => {
        if (value) {
            setSelectedProcessTemplateSlug(value);
            setProcessTemplateSlugProperty(value);
            setVersionMaskProperty("");
            processTemplateConsumerActionDispatch("Select Process Template", { type: "Select Process Template", processTemplateId: value, action: Action.Add });
        }
    };
    return (<>
            <ExpandableFormSection isExpandedByDefault={isNew} errorKey="Name" title="Name" summary={buildNameSummary(action)} key="Name" help="A short, memorable, unique name for this process template.">
                <DebounceText value={action.Name} onChange={setName} label="Name" error={getFieldError("Name")} validate={required("Please enter a name")} autoFocus={true}/>
                {!isNew && (<SlugEditor value={action.Slug ?? generateSlug(action.Name)} name={action.Name} originalSlug={cleanAction?.Slug ?? ""} onChange={setSlug} label="Slug" error={getFieldError("Slug")} validate={required("Please enter a slug")}/>)}
            </ExpandableFormSection>
            <FormSectionHeading title="Process template details"/>
            <ProcessTemplateSelector selectedProcessTemplateSlug={selectedProcessTemplateSlug} setSelectedProcessTemplateSlug={setSelectedProcessTemplateSlug} distinctTemplates={distinctTemplates} isNew={isNew} isProcessTemplateUnavailable={isProcessTemplateUnavailable} onProcessTemplateChanged={onProcessTemplateChanged}/>
            {selectedProcessTemplate ? (<>
                    {isEmpty(selectedProcessTemplate.Description) ? null : <FormSection title={"Description"} includeBorder={true} help={selectedProcessTemplate.Description}></FormSection>}
                    <UseCloudTargetDiscoverySection processTemplate={selectedProcessTemplate} processVariableSet={processVariableSet} libraryVariableSets={libraryVariableSets}/>
                </>) : null}
            {selectedProcessTemplate && latestProcessTemplateVersionSummary ? (<ProcessTemplateVersioningSection key={latestProcessTemplateVersionSummary.ProcessTemplateSlug} initialActionProperties={initialActionProperties} initialProcessTemplateVersion={initialProcessTemplateVersion} selectedProcessTemplateDetails={selectedProcessTemplateDetails} setVersionMaskProperty={setVersionMaskProperty} latestProcessTemplateVersionSummary={latestProcessTemplateVersionSummary} selectedUpdateBehaviour={selectedUpdateBehaviour} setSelectedUpdateBehaviour={setSelectedUpdateBehaviour} selectedUsageConstraint={selectedUsageConstraint} setSelectedUsageConstraint={setSelectedUsageConstraint} processTemplateConsumerActionDispatch={processTemplateConsumerActionDispatch} processTemplateSlug={selectedProcessTemplate.Slug} onUpdateToLatestVersion={onUpdateToLatestVersion} currentVersion={currentVersion} templateName={selectedProcessTemplate.Name}/>) : null}
        </>);
}
interface UseCloudTargetDiscoverySectionProps {
    processVariableSet: VariableSetResource | FailedPermissionCheck;
    libraryVariableSets: LoadedLibraryVariableSets[] | FailedPermissionCheck;
    processTemplate: ProcessTemplateResource | undefined;
}
function UseCloudTargetDiscoverySection({ processTemplate, processVariableSet, libraryVariableSets }: UseCloudTargetDiscoverySectionProps) {
    if (!processTemplate || isFailedPermissionCheck(processVariableSet) || isFailedPermissionCheck(libraryVariableSets)) {
        return null;
    }
    const cloudTargetDiscoveryActionTypes = new Set(pluginRegistry
        .getAllActions()
        .filter((a) => a.targetDiscoveryCloudConnectionProviders)
        .map((a) => a.actionType));
    const processTemplateUsesCloudTargetDiscovery = processTemplate.Steps.some((s) => s.Actions.some((a) => cloudTargetDiscoveryActionTypes.has(a.ActionType)));
    if (!processTemplateUsesCloudTargetDiscovery) {
        return null;
    }
    const connectionType = CloudConnectionUsageType.TargetDiscovery;
    const cloudConnections = getCloudConnections(["Azure", "Aws"], connectionType, processVariableSet, libraryVariableSets);
    const isEnabled = isCloudTargetDiscoveryEnabled(cloudConnections, connectionType);
    return isEnabled ? <FormSection title="Using Cloud Target Discovery" help="Yes" includeBorder={true}/> : null;
}
function mapMaskToUsageConstraint(value: string): UsageConstraint {
    if (value.toLowerCase() === "prerelease") {
        return UsageConstraint.PreRelease;
    }
    return UsageConstraint.Stable;
}
